import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import businessService from '../services/businessService';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

const COUNTRIES = [
  'USA',
  'Canada',
  'United Kingdom',
  'Australia',
  'Germany',
  'France',
  'Spain',
  'Italy',
  'Netherlands',
  'Other'
];

function BusinessBasics() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    business_name: '',
    owner_name: '',
    website: '',
    sales_page_url: '',
    industry: '',
    customer_type: '',
    business_scope: '',
    primary_location: '',
    core_product: '',
    features_benefits: '',
    business_stage: '',
    monthly_revenue: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showReassurance, setShowReassurance] = useState(
    !localStorage.getItem('hideReassuranceBanner')
  );

  const dismissReassurance = () => {
    localStorage.setItem('hideReassuranceBanner', 'true');
    setShowReassurance(false);
  };

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load existing data
    const result = await businessService.getBusinessBasics();
    if (result.success && result.data.fields) {
      setFormData({
        business_name: result.data.fields.business_name || '',
        owner_name: result.data.fields.owner_name || '',
        website: result.data.fields.website || '',
        sales_page_url: result.data.fields.sales_page_url || '',
        industry: result.data.fields.industry || '',
        customer_type: result.data.fields.customer_type || '',
        business_scope: result.data.fields.business_scope || '',
        primary_location: result.data.fields.primary_location || '',
        core_product: result.data.fields.core_product || '',
        features_benefits: result.data.fields.features_benefits || '',
        business_stage: result.data.fields.business_stage || '',
        monthly_revenue: result.data.fields.monthly_revenue || ''
      });
    }

    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: null });
    }

    // Clear primary_location when business_scope changes
    if (name === 'business_scope') {
      setFormData({
        ...formData,
        [name]: value,
        primary_location: '' // Reset location when scope changes
      });
      // Clear location error too
      if (fieldErrors.primary_location) {
        setFieldErrors({ ...fieldErrors, primary_location: null });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleBlur = async (e) => {
    // Auto-save on blur
    const { name, value } = e.target;
    if (!value) return; // Don't auto-save empty fields

    setAutoSaving(true);
    await businessService.saveField(name, value);
    setAutoSaving(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setFieldErrors({});

    // Field labels for error messages
    const fieldLabels = {
      business_name: 'Business Name',
      owner_name: "Owner's Name",
      industry: 'Industry',
      customer_type: 'Customer Type',
      business_scope: 'Business Scope',
      primary_location: formData.business_scope === 'national' ? 'Country' :
                        formData.business_scope === 'local' ? 'Primary Location' :
                        'Primary Markets/Regions',
      core_product: 'Core Product/Service',
      features_benefits: 'Features and Benefits',
      business_stage: 'Business Stage'
    };

    // Validate all required fields
    const required = [
      'business_name',
      'owner_name',
      'industry',
      'customer_type',
      'business_scope',
      'primary_location',
      'core_product',
      'features_benefits',
      'business_stage'
    ];

    const errors = {};
    const missingFields = [];

    for (const field of required) {
      if (!formData[field]) {
        errors[field] = `${fieldLabels[field]} is required`;
        missingFields.push(fieldLabels[field]);
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);

    const result = await businessService.saveBusinessBasics(formData);

    // Also create/update a Product record with position='core'
    // This ensures guided journey users have a product in the Product table
    if (result.success && formData.core_product) {
      try {
        // Extract product name from description (first line or first 100 chars)
        const description = formData.core_product.trim();
        const firstLine = description.split('\n')[0];
        const productName = firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;

        // Check if core product already exists in Product table
        const productsRes = await fetch('/api/products/get-catalog', { credentials: 'include' });
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          const products = productsData.catalog || [];
          const existingCore = products.find(p => p.position?.toLowerCase() === 'core');

          if (existingCore) {
            // Update existing core product name
            await fetch(`/api/products/${existingCore.id}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ name: productName })
            });
          } else {
            // Create new core product
            await fetch('/api/products/save-initial-products', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                products: [{
                  name: productName,
                  type: 'digital',
                  purchase_type: 'one-time',
                  position: 'core',
                  sale_price: '',
                  cost_to_deliver: ''
                }]
              })
            });
          }
        }
      } catch (err) {
        console.error('Error saving core product to Product table:', err);
        // Non-critical, don't block the main save
      }
    }

    setSaving(false);

    if (result.success) {
      setSuccess('Business basics saved successfully!');
      setTimeout(() => {
        navigate('/dashboard/foundation');
      }, 1500);
    } else {
      setError(result.error);
    }
  };

  const renderLocationField = () => {
    switch (formData.business_scope) {
      case 'local':
        return (
          <Form.Group className="mb-3" controlId="primary_location">
            <Form.Label>Primary Location *</Form.Label>
            <Form.Control
              type="text"
              name="primary_location"
              value={formData.primary_location}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="e.g., Portland, Oregon, USA"
              isInvalid={!!fieldErrors.primary_location}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.primary_location}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Where do you primarily serve customers?
            </Form.Text>
          </Form.Group>
        );

      case 'national':
        return (
          <Form.Group className="mb-3" controlId="primary_location">
            <Form.Label>Country *</Form.Label>
            <Form.Select
              name="primary_location"
              value={formData.primary_location}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              isInvalid={!!fieldErrors.primary_location}
            >
              <option value="">Select a country</option>
              {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </Form.Select>
            {fieldErrors.primary_location && (
              <Form.Control.Feedback type="invalid">
                {fieldErrors.primary_location}
              </Form.Control.Feedback>
            )}
            <Form.Text className="text-muted">
              Which country do you operate in?
            </Form.Text>
          </Form.Group>
        );

      case 'international':
        return (
          <Form.Group className="mb-3" controlId="primary_location">
            <Form.Label>Primary Markets/Regions *</Form.Label>
            <Form.Control
              type="text"
              name="primary_location"
              value={formData.primary_location}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="e.g., North America, Europe, Asia-Pacific"
              isInvalid={!!fieldErrors.primary_location}
            />
            <Form.Control.Feedback type="invalid">
              {fieldErrors.primary_location}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Which countries or regions do you serve?
            </Form.Text>
          </Form.Group>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="foundation" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Business Basics</h2>
                {autoSaving && (
                  <small className="text-muted">Saving...</small>
                )}
              </div>

              <p className="text-muted mb-4">
                Let's start with the fundamentals of your business. This information will be used throughout the application to generate personalized marketing content.
              </p>

              {showReassurance && (
                <Alert variant="info" className="d-flex justify-content-between align-items-start">
                  <div>
                    <strong>💡 Don't overthink it!</strong>
                    <p className="mb-0 small">Everything can be changed later. Just get your best guess down - we'll refine as we go.</p>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    aria-label="Dismiss"
                    onClick={dismissReassurance}
                    style={{ fontSize: '0.75rem' }}
                  />
                </Alert>
              )}

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="business_name">
                  <Form.Label>Business Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    maxLength={100}
                    placeholder="Enter your business name"
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="owner_name">
                  <Form.Label>Owner's Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="owner_name"
                    value={formData.owner_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    maxLength={100}
                    placeholder="Enter your name"
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="website">
                  <Form.Label>Website URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={500}
                    placeholder="https://yourbusiness.com"
                  />
                  <Form.Text className="text-muted">
                    Your main business website
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="sales_page_url">
                  <Form.Label>Sales Page URL</Form.Label>
                  <Form.Control
                    type="url"
                    name="sales_page_url"
                    value={formData.sales_page_url}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxLength={500}
                    placeholder="https://yourbusiness.com/sales"
                  />
                  <Form.Text className="text-muted">
                    Your primary sales or landing page (used in social post CTAs)
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="industry">
                  <Form.Label>Industry *</Form.Label>
                  <Form.Control
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    maxLength={100}
                    placeholder="e.g., Health & Wellness, SaaS, E-commerce, Consulting"
                  />
                  <Form.Text className="text-muted">
                    What industry or category does your business operate in?
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="customer_type">
                  <Form.Label>Who do you serve? *</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="radio"
                      name="customer_type"
                      value="b2b"
                      label="Businesses (B2B)"
                      checked={formData.customer_type === 'b2b'}
                      onChange={handleChange}
                      id="customer-b2b"
                    />
                    <Form.Check
                      type="radio"
                      name="customer_type"
                      value="b2c"
                      label="Consumers (B2C)"
                      checked={formData.customer_type === 'b2c'}
                      onChange={handleChange}
                      id="customer-b2c"
                    />
                    <Form.Check
                      type="radio"
                      name="customer_type"
                      value="both"
                      label="Both"
                      checked={formData.customer_type === 'both'}
                      onChange={handleChange}
                      id="customer-both"
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="business_scope">
                  <Form.Label>Business Scope *</Form.Label>
                  <div>
                    <Form.Check
                      type="radio"
                      name="business_scope"
                      value="local"
                      label="Local/Regional (specific city, county, or region)"
                      checked={formData.business_scope === 'local'}
                      onChange={handleChange}
                      id="scope-local"
                    />
                    <Form.Check
                      type="radio"
                      name="business_scope"
                      value="national"
                      label="National (entire country)"
                      checked={formData.business_scope === 'national'}
                      onChange={handleChange}
                      id="scope-national"
                    />
                    <Form.Check
                      type="radio"
                      name="business_scope"
                      value="international"
                      label="International (multiple countries or global)"
                      checked={formData.business_scope === 'international'}
                      onChange={handleChange}
                      id="scope-international"
                    />
                  </div>
                </Form.Group>

                {renderLocationField()}

                <Form.Group className="mb-3" controlId="core_product">
                  <Form.Label>Core Product/Service *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    name="core_product"
                    value={formData.core_product}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    maxLength={1000}
                    placeholder="Describe your main product or service"
                  />
                </Form.Group>

                <Form.Group className="mb-4" controlId="features_benefits">
                  <Form.Label>Features and Benefits *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    name="features_benefits"
                    value={formData.features_benefits}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    maxLength={1200}
                    placeholder="List the key features and benefits of your product/service"
                  />
                  <Form.Text className="text-muted">
                    What makes your offering valuable to customers?
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="business_stage">
                  <Form.Label>How long have you been in business? *</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="radio"
                      name="business_stage"
                      value="idea"
                      label="Just an idea (haven't launched yet)"
                      checked={formData.business_stage === 'idea'}
                      onChange={handleChange}
                      id="stage-idea"
                    />
                    <Form.Check
                      type="radio"
                      name="business_stage"
                      value="0-6-months"
                      label="0-6 months (just launched)"
                      checked={formData.business_stage === '0-6-months'}
                      onChange={handleChange}
                      id="stage-0-6"
                    />
                    <Form.Check
                      type="radio"
                      name="business_stage"
                      value="6-12-months"
                      label="6-12 months (first year)"
                      checked={formData.business_stage === '6-12-months'}
                      onChange={handleChange}
                      id="stage-6-12"
                    />
                    <Form.Check
                      type="radio"
                      name="business_stage"
                      value="1-2-years"
                      label="1-2 years (early stage)"
                      checked={formData.business_stage === '1-2-years'}
                      onChange={handleChange}
                      id="stage-1-2"
                    />
                    <Form.Check
                      type="radio"
                      name="business_stage"
                      value="2-5-years"
                      label="2-5 years (growth stage)"
                      checked={formData.business_stage === '2-5-years'}
                      onChange={handleChange}
                      id="stage-2-5"
                    />
                    <Form.Check
                      type="radio"
                      name="business_stage"
                      value="5-plus-years"
                      label="5+ years (established)"
                      checked={formData.business_stage === '5-plus-years'}
                      onChange={handleChange}
                      id="stage-5-plus"
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-4" controlId="monthly_revenue">
                  <Form.Label>If already in business, what's your current monthly revenue? (Optional)</Form.Label>
                  <div className="mt-2">
                    <Form.Check
                      type="radio"
                      name="monthly_revenue"
                      value="pre-revenue"
                      label="$0 (pre-revenue)"
                      checked={formData.monthly_revenue === 'pre-revenue'}
                      onChange={handleChange}
                      id="revenue-0"
                    />
                    <Form.Check
                      type="radio"
                      name="monthly_revenue"
                      value="1-5k"
                      label="$1-5K/month"
                      checked={formData.monthly_revenue === '1-5k'}
                      onChange={handleChange}
                      id="revenue-1-5k"
                    />
                    <Form.Check
                      type="radio"
                      name="monthly_revenue"
                      value="5-25k"
                      label="$5K-25K/month"
                      checked={formData.monthly_revenue === '5-25k'}
                      onChange={handleChange}
                      id="revenue-5-25k"
                    />
                    <Form.Check
                      type="radio"
                      name="monthly_revenue"
                      value="25-100k"
                      label="$25K-100K/month"
                      checked={formData.monthly_revenue === '25-100k'}
                      onChange={handleChange}
                      id="revenue-25-100k"
                    />
                    <Form.Check
                      type="radio"
                      name="monthly_revenue"
                      value="100-500k"
                      label="$100K-500K/month"
                      checked={formData.monthly_revenue === '100-500k'}
                      onChange={handleChange}
                      id="revenue-100-500k"
                    />
                    <Form.Check
                      type="radio"
                      name="monthly_revenue"
                      value="500k-plus"
                      label="$500K+/month"
                      checked={formData.monthly_revenue === '500k-plus'}
                      onChange={handleChange}
                      id="revenue-500k-plus"
                    />
                    <Form.Check
                      type="radio"
                      name="monthly_revenue"
                      value="prefer-not-say"
                      label="Prefer not to say"
                      checked={formData.monthly_revenue === 'prefer-not-say'}
                      onChange={handleChange}
                      id="revenue-prefer-not"
                    />
                  </div>
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/foundation')}
                  >
                    Save for Later
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save and Continue'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default BusinessBasics;
